import { useState, useEffect } from 'react'
import { RefreshCw, CheckCircle, XCircle, Clock, Download, Filter, Database, Users, Activity, AlertTriangle, Eye, UserX } from 'lucide-react'
import { adminAPI } from '../services/api'

const Logs = () => {
  const [syncStats, setSyncStats] = useState({
    total_operations: "0",
    total_users_processed: "0", 
    total_successful: "0",
    total_errors: "0",
    avg_duration_ms: "0",
    last_sync: null
  })
  const [recentErrors, setRecentErrors] = useState([])
  const [unsyncedUsers, setUnsyncedUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('stats')

  useEffect(() => {
    fetchAllData()
  }, [])

  const fetchAllData = async () => {
    try {
      setLoading(true)
      await Promise.all([
        fetchSyncStats(),
        fetchRecentErrors(),
        fetchUnsyncedUsers()
      ])
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchSyncStats = async () => {
    try {
      const response = await adminAPI.getSyncStats()
      if (response.data.success) {
        setSyncStats(response.data.stats)
      }
    } catch (error) {
      console.error('Error fetching sync stats:', error)
    }
  }

  const fetchRecentErrors = async () => {
    try {
      const response = await adminAPI.getRecentErrors()
      if (response.data.success) {
        setRecentErrors(response.data.errors)
      }
    } catch (error) {
      console.error('Error fetching recent errors:', error)
    }
  }

  const fetchUnsyncedUsers = async () => {
    try {
      const response = await adminAPI.getUnsyncedUsers()
      if (response.data.success) {
        setUnsyncedUsers(response.data.users)
      }
    } catch (error) {
      console.error('Error fetching unsynced users:', error)
    }
  }

  const handleRefresh = async () => {
    await fetchAllData()
  }

  const handleExport = () => {
    // Логіка експорту статистики
    const exportData = {
      syncStats,
      recentErrors,
      unsyncedUsers,
      exportDate: new Date().toISOString()
    }
    const dataStr = JSON.stringify(exportData, null, 2)
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr)
    
    const exportFileDefaultName = `euromix-sync-data-${new Date().toISOString().split('T')[0]}.json`
    
    const linkElement = document.createElement('a')
    linkElement.setAttribute('href', dataUri)
    linkElement.setAttribute('download', exportFileDefaultName)
    linkElement.click()
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'Ніколи'
    return new Date(dateString).toLocaleString('uk-UA')
  }

  const formatDuration = (ms) => {
    if (!ms || ms < 1000) return `${Math.round(ms || 0)}мс`
    return `${(ms / 1000).toFixed(1)}с`
  }

  const getSyncStatusBadge = (lastSync) => {
    if (!lastSync) return 'bg-gray-100 text-gray-800'
    
    const now = new Date()
    const syncTime = new Date(lastSync)
    const diffHours = (now - syncTime) / (1000 * 60 * 60)
    
    if (diffHours < 1) return 'bg-green-100 text-green-800'
    if (diffHours < 24) return 'bg-yellow-100 text-yellow-800'
    return 'bg-red-100 text-red-800'
  }

  const getSyncStatusText = (lastSync) => {
    if (!lastSync) return 'Ніколи'
    
    const now = new Date()
    const syncTime = new Date(lastSync)
    const diffHours = (now - syncTime) / (1000 * 60 * 60)
    
    if (diffHours < 1) return 'Нещодавно'
    if (diffHours < 24) return 'Сьогодні'
    return 'Давно'
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Синхронізація з 1С</h1>
          <p className="text-gray-600">Моніторинг синхронізації користувачів з системою 1С</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={handleExport}
            className="btn btn-secondary flex items-center space-x-2"
          >
            <Download size={20} />
            <span>Експорт</span>
          </button>
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="btn btn-primary flex items-center space-x-2"
          >
            <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
            <span>Оновити</span>
          </button>
        </div>
      </div>

      {/* Табы */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('stats')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'stats'
                ? 'border-gray-900 text-gray-900'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Activity size={16} />
              <span>Статистика</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('errors')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'errors'
                ? 'border-gray-900 text-gray-900'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center space-x-2">
              <AlertTriangle size={16} />
              <span>Останні помилки</span>
              {recentErrors.length > 0 && (
                <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full">
                  {recentErrors.length}
                </span>
              )}
            </div>
          </button>
          <button
            onClick={() => setActiveTab('unsynced')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'unsynced'
                ? 'border-gray-900 text-gray-900'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center space-x-2">
              <UserX size={16} />
              <span>Несинхронізовані</span>
              {unsyncedUsers.length > 0 && (
                <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full">
                  {unsyncedUsers.length}
                </span>
              )}
            </div>
          </button>
        </nav>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      ) : (
        <>
          {/* Статистика */}
          {activeTab === 'stats' && (
          <div className="card p-6">
              <div className="flex items-center space-x-2 mb-6">
                <RefreshCw className="h-5 w-5 text-blue-600" />
                <h3 className="text-lg font-semibold text-gray-900">Статистика синхронізації</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
              <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900">{syncStats.total_operations || 0}</p>
                <p className="text-sm text-gray-600">Операцій</p>
              </div>
              <div className="text-center">
                  <p className="text-2xl font-bold text-blue-600">{parseInt(syncStats.total_users_processed || 0).toLocaleString()}</p>
                <p className="text-sm text-gray-600">Користувачів оброблено</p>
              </div>
              <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">{parseInt(syncStats.total_successful || 0).toLocaleString()}</p>
                <p className="text-sm text-gray-600">Успішних</p>
              </div>
              <div className="text-center">
                  <p className="text-2xl font-bold text-red-600">{parseInt(syncStats.total_errors || 0).toLocaleString()}</p>
                <p className="text-sm text-gray-600">Помилок</p>
              </div>
              <div className="text-center">
                  <p className="text-2xl font-bold text-purple-600">{formatDuration(parseFloat(syncStats.avg_duration_ms || 0))}</p>
                <p className="text-sm text-gray-600">Середній час</p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center space-x-2">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getSyncStatusBadge(syncStats.last_sync)}`}>
                      {getSyncStatusText(syncStats.last_sync)}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mt-1">Остання синхронізація</p>
                  <p className="text-xs text-gray-500">{formatDate(syncStats.last_sync)}</p>
                </div>
            </div>

              {/* Прогресс бары */}
            <div className="border-t border-gray-200 pt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Ефективність синхронізації</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Успішність:</span>
                      <span className="font-medium">
                          {parseInt(syncStats.total_users_processed || 0) > 0 
                            ? ((parseInt(syncStats.total_successful || 0) / parseInt(syncStats.total_users_processed || 1)) * 100).toFixed(1)
                          : 0}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-green-600 h-2 rounded-full" 
                        style={{ 
                            width: `${parseInt(syncStats.total_users_processed || 0) > 0 
                              ? ((parseInt(syncStats.total_successful || 0) / parseInt(syncStats.total_users_processed || 1)) * 100)
                            : 0}%` 
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Статистика помилок</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Помилки:</span>
                      <span className="font-medium">
                          {parseInt(syncStats.total_users_processed || 0) > 0 
                            ? ((parseInt(syncStats.total_errors || 0) / parseInt(syncStats.total_users_processed || 1)) * 100).toFixed(1)
                          : 0}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-red-600 h-2 rounded-full" 
                        style={{ 
                            width: `${parseInt(syncStats.total_users_processed || 0) > 0 
                              ? ((parseInt(syncStats.total_errors || 0) / parseInt(syncStats.total_users_processed || 1)) * 100)
                            : 0}%` 
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          )}

          {/* Ошибки */}
          {activeTab === 'errors' && (
            <div className="card p-6">
              <div className="flex items-center space-x-2 mb-4">
                <AlertTriangle className="h-5 w-5 text-red-600" />
                <h3 className="text-lg font-semibold text-gray-900">Останні помилки синхронізації</h3>
              </div>
              
              {recentErrors.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle size={48} className="mx-auto text-green-500 mb-4" />
                  <p className="text-gray-500">Помилок не знайдено</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {recentErrors.map((error) => (
                    <div key={error.id} className="border border-red-200 rounded-lg p-4 bg-red-50">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <h4 className="font-medium text-gray-900">Операція #{error.id}</h4>
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
                              {error.operation_type}
                            </span>
                          </div>
                          
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                            <div>
                              <p className="text-sm text-gray-600">Користувачів:</p>
                              <p className="font-medium">{error.users_count}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-600">Успішних:</p>
                              <p className="font-medium text-green-600">{error.success_count}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-600">Помилок:</p>
                              <p className="font-medium text-red-600">{error.error_count}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-600">Час:</p>
                              <p className="font-medium">{formatDuration(error.duration_ms)}</p>
                            </div>
                          </div>
                          
                          <div className="bg-white rounded p-3 mb-2">
                            <p className="text-sm text-gray-700">{error.error_details}</p>
                          </div>
                          
                          <div className="flex items-center space-x-4 text-xs text-gray-500">
                            <span>Початок: {formatDate(error.started_at)}</span>
                            <span>Завершення: {formatDate(error.completed_at)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Несинхронизированные пользователи */}
          {activeTab === 'unsynced' && (
            <div className="card p-6">
              <div className="flex items-center space-x-2 mb-4">
                <UserX className="h-5 w-5 text-yellow-600" />
                <h3 className="text-lg font-semibold text-gray-900">Несинхронізовані користувачі</h3>
              </div>
              
              {unsyncedUsers.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle size={48} className="mx-auto text-green-500 mb-4" />
                  <p className="text-gray-500">Всі користувачі синхронізовані</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="table w-full">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Телефон</th>
                        <th>Ім'я</th>
                        <th>Номер картки</th>
                        <th>Дата народження</th>
                        <th>Email</th>
                        <th>Регіон</th>
                        <th>Статус</th>
                      </tr>
                    </thead>
                    <tbody>
                      {unsyncedUsers.map((user) => (
                        <tr key={user.id}>
                          <td>{user.id}</td>
                          <td className="font-mono">{user.phone || '-'}</td>
                          <td>{user.customerName || '-'}</td>
                          <td className="font-mono">{user.cardNumber || '-'}</td>
                          <td>{user.birthday || '-'}</td>
                          <td>{user.email || '-'}</td>
                          <td>{user.region || '-'}</td>
                          <td>
                            <span className={`badge ${
                              user.status === 'active' ? 'badge-success' : 
                              user.status === 'inactive' ? 'badge-error' : 
                              'badge-info'
                            }`}>
                              {user.status === 'active' ? 'Активний' : 
                               user.status === 'inactive' ? 'Неактивний' : 
                               user.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default Logs
